import React from "react";
import { Link } from "react-router-dom";
import Seo from "../lib/Seo";
import {
  Award, BookOpen, Users, Sparkles, ArrowRight, Compass, Briefcase, Heart, HeartPulse,
  GraduationCap, FileText, Gem, PenLine, ShieldCheck, Video, Headphones, Lock, Feather,
  HandHeart, Target, Eye, MessageCircle, Star,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { waLink } from "../lib/api";

const PORTRAIT = "/assets/bhupaal-bisht.jpg";

const offerings = [
  { icon: Compass, title: "Personal Horoscope Consultation", desc: "In-depth birth chart analysis with clear, actionable guidance." },
  { icon: Briefcase, title: "Career & Business Guidance", desc: "Timing, direction and remedies for your professional path." },
  { icon: Heart, title: "Marriage & Relationship Analysis", desc: "Compatibility, Guna Milan and relationship timing." },
  { icon: HeartPulse, title: "Health & Medical Astrology", desc: "Astrological view on chronic issues, vitality and prevention." },
  { icon: GraduationCap, title: "Educational Astrology Courses", desc: "Live and recorded programs to learn Jyotish from scratch." },
  { icon: FileText, title: "Personalized Remedies", desc: "Custom mantras, upayas and rituals drawn from classical texts." },
  { icon: Gem, title: "Gemstone & Rudraksha Guidance", desc: "Classical Ratna and Rudraksha prescription based on your chart." },
  { icon: PenLine, title: "Astrology Articles & Blogs", desc: "Regular writings on Nakshatras, remedies and predictive frameworks." },
];

const reasons = [
  { icon: ShieldCheck, title: "10+ Years of Experience" },
  { icon: Sparkles, title: "Authentic Vedic Astrology" },
  { icon: Compass, title: "Personalized Guidance" },
  { icon: FileText, title: "Practical Remedies" },
  { icon: Video, title: "Live Interactive Classes" },
  { icon: BookOpen, title: "PDF Study Material" },
  { icon: Headphones, title: "Recorded Sessions" },
  { icon: Users, title: "Student Support" },
  { icon: Lock, title: "Ethical & Confidential Consultations" },
];

const values = [
  { icon: Feather, title: "Authenticity", desc: "Rooted in classical Parashari lineage — no shortcuts, no folklore." },
  { icon: ShieldCheck, title: "Integrity", desc: "Only remedies and predictions that can be honestly stood behind." },
  { icon: HandHeart, title: "Compassion", desc: "Every seeker is met with warmth, patience and respect." },
  { icon: GraduationCap, title: "Continuous Learning", desc: "Ongoing study of shastras, deeper research, refined practice." },
  { icon: Users, title: "Client-Centric Guidance", desc: "Your questions define the session — we listen before we speak." },
  { icon: Sparkles, title: "Respect for Ancient Wisdom", desc: "Timeless knowledge is preserved and taught with reverence." },
];

/* Cosmic background — subtle zodiac wheel + constellations */
function CosmicBackdrop() {
  const signs = ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"];
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] via-[#0B1F3A] to-[#062036]" />
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#0E9F6E]/10 blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
      {/* Stars */}
      {[...Array(38)].map((_, i) => (
        <span key={i} className="star" style={{
          width: `${Math.random() * 2 + 1}px`,
          height: `${Math.random() * 2 + 1}px`,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 4}s`,
        }} />
      ))}
      {/* Subtle zodiac wheel on the right */}
      <svg viewBox="0 0 500 500" className="absolute -right-24 top-1/2 -translate-y-1/2 w-[560px] opacity-[0.15] wheel-spin">
        <circle cx="250" cy="250" r="220" fill="none" stroke="#D4AF37" strokeWidth="1.2" strokeDasharray="2 6" />
        <circle cx="250" cy="250" r="180" fill="none" stroke="#D4AF37" strokeOpacity="0.6" />
        <circle cx="250" cy="250" r="140" fill="none" stroke="#10B981" strokeOpacity="0.55" />
        {[...Array(60)].map((_, i) => {
          const a = (i / 60) * Math.PI * 2;
          const x1 = 250 + Math.cos(a) * 214;
          const y1 = 250 + Math.sin(a) * 214;
          const x2 = 250 + Math.cos(a) * 220;
          const y2 = 250 + Math.sin(a) * 220;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#D4AF37" strokeWidth="1" />;
        })}
        {signs.map((s, i) => {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const x = 250 + Math.cos(a) * 200;
          const y = 250 + Math.sin(a) * 200;
          return (
            <text key={s} x={x} y={y} textAnchor="middle" dominantBaseline="central" fill="#D4AF37" fontSize="22" fontFamily="serif"
              transform={`rotate(${(i / 12) * 360 + 90}, ${x}, ${y})`}>{s}</text>
          );
        })}
      </svg>
      {/* Left constellation lines */}
      <svg viewBox="0 0 500 500" className="absolute -left-20 top-24 w-[420px] opacity-25">
        <g stroke="#F5E8D8" strokeOpacity="0.7" strokeWidth="0.8" fill="none">
          <polyline points="120,110 155,140 195,130 230,155" />
          <polyline points="90,340 130,360 160,340 200,375" />
          <polyline points="220,80 260,130 305,110 340,150" />
        </g>
        {[[120,110],[155,140],[195,130],[230,155],[90,340],[130,360],[160,340],[200,375],[220,80],[260,130],[305,110],[340,150]]
          .map(([x,y], i) => <circle key={i} cx={x} cy={y} r="1.6" fill="#F5E8D8" opacity="0.9" />)}
      </svg>
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page" className="min-h-screen">
      <Seo
        title="About Bhupaal Bisht — Vedic Astrologer & Founder | YourAstroHub"
        description="Meet Bhupaal Bisht, Vedic Astrologer & Founder of YourAstroHub. 10+ years of authentic Jyotish practice — consultations, courses, remedies, and mentorship for a happier, more successful life."
        keywords="Bhupaal Bisht, About YourAstroHub, Vedic Astrologer India, Jyotish Mentor, Founder YourAstroHub"
        path="/about"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "About", path: "/about" },
        ]}
        schemas={[{
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Bhupaal Bisht",
          jobTitle: "Vedic Astrologer, Astrology Mentor, Founder of YourAstroHub",
          image: "https://vedic-destiny-hub-2.preview.emergentagent.com/assets/bhupaal-bisht.jpg",
          worksFor: { "@type": "Organization", name: "YourAstroHub" },
          knowsAbout: ["Vedic Astrology", "Jyotish", "Nakshatra", "Panchang", "Medical Astrology", "Remedies"],
          sameAs: ["https://www.facebook.com/share/1DyRBEjWr3/"],
        }]}
      />
      {/* HERO ---------------------------------------------------------- */}
      <section className="relative overflow-hidden -mt-20 pt-32 md:pt-40 pb-24 md:pb-32 text-[#F5E8D8] bg-[#0B1F3A]">
        <CosmicBackdrop />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-8 text-center">
          <div className="mb-6 flex justify-center">
            <img
              src="/assets/logo-full.png"
              alt="YourAstroHub - Vedic Astrology Consultation and Education Logo"
              className="h-28 md:h-40 w-auto drop-shadow-[0_0_30px_rgba(212,175,55,0.35)]"
              width="240" height="240"
            />
          </div>
          <div className="reveal eyebrow flex items-center justify-center gap-3 mb-6">
            <span className="w-8 gold-hairline" /> About YourAstroHub <span className="w-8 gold-hairline" />
          </div>
          <h1 data-testid="about-hero-title" className="reveal-2 font-serif text-5xl md:text-7xl font-light leading-[1.05]">
            About <span className="gold-text italic">YourAstroHub</span>
          </h1>
          <p className="reveal-3 mt-6 text-lg md:text-xl text-[#F5E8D8]/75 max-w-3xl mx-auto leading-relaxed">
            Guiding lives through the timeless wisdom of authentic Vedic Astrology.
          </p>
        </div>
      </section>

      {/* MEET YOUR MENTOR ---------------------------------------------- */}
      <section data-testid="meet-mentor" className="py-24 md:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="eyebrow mb-3">Meet Your Mentor</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">
              A guide devoted to <span className="gold-text italic">clarity.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="relative">
                <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-[#D4AF37]/30 via-transparent to-[#10B981]/30 blur-2xl" />
                <div className="relative rounded-3xl overflow-hidden aspect-[3/4] border border-[#D4AF37]/30 grain">
                  <img src={PORTRAIT} alt="Bhupaal Bisht — Founder & Mentor of YourAstroHub" className="w-full h-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#0B1F3A] via-[#0B1F3A]/70 to-transparent">
                    <div className="text-[#D4AF37] text-[10px] uppercase tracking-[0.24em]">Founder & Mentor</div>
                    <div className="text-[#F5E8D8] font-serif text-2xl mt-1">Bhupaal Bisht</div>
                    <div className="text-[#F5E8D8]/70 text-xs mt-1">Vedic Astrologer · Astrology Mentor</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-7 space-y-5 text-foreground/80 text-base md:text-lg leading-relaxed">
              <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-2">Bhupaal Bisht</h3>
              <p className="text-sm uppercase tracking-[0.22em] text-[#0E9F6E] font-semibold">
                Vedic Astrologer &nbsp;·&nbsp; Astrology Mentor &nbsp;·&nbsp; Founder of YourAstroHub
              </p>

              <p>
                Welcome to <b>YourAstroHub</b>, where ancient Vedic wisdom meets practical guidance for modern life.
              </p>
              <p>
                I am <b>Bhupaal Bisht</b>, a passionate Vedic astrologer dedicated to helping people understand their
                life&apos;s purpose and make better decisions through the science of Jyotish.
              </p>
              <p>
                For over <b>10 years</b>, I have guided individuals from different walks of life in areas such as
                career, marriage, business, finances, education, health, and spiritual growth. My approach combines
                classical Vedic astrology with practical, easy-to-follow remedies that can be applied in everyday life.
              </p>
              <p>
                Along with consultations, I am committed to teaching authentic Vedic Astrology through structured
                online courses, enabling students to confidently analyze horoscopes and use astrology responsibly.
              </p>

              <div className="grid grid-cols-3 gap-4 mt-8">
                {[[Award, "Classical", "Parashari lineage"], [Users, "5,000+", "consultations"], [BookOpen, "1,200+", "students taught"]].map(([Icon, big, small]) => (
                  <div key={small} className="p-5 rounded-2xl border border-[#D4AF37]/25 bg-background">
                    <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                    <div className="font-serif text-2xl">{big}</div>
                    <div className="text-xs text-foreground/60 mt-1">{small}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION ---------------------------------------------- */}
      <section data-testid="mission-vision" className="py-24 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl p-10 bg-background border border-[#D4AF37]/25 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#D4AF37]/15 blur-2xl" />
            <Target className="w-8 h-8 text-[#D4AF37] mb-5" />
            <div className="eyebrow mb-2">Our Mission</div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">Wisdom made simple, practical & accessible.</h3>
            <p className="text-foreground/75 leading-relaxed">
              To make authentic Vedic Astrology simple, practical, and accessible while helping individuals achieve
              clarity, confidence, and happiness through accurate guidance and ethical practice.
            </p>
          </div>
          <div className="rounded-3xl p-10 bg-background border border-[#10B981]/30 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#10B981]/20 blur-2xl" />
            <Eye className="w-8 h-8 text-[#0E9F6E] mb-5" />
            <div className="eyebrow mb-2" style={{ color: "#0E9F6E" }}>Our Vision</div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">A trusted global learning platform.</h3>
            <p className="text-foreground/75 leading-relaxed">
              To build a trusted global learning platform where students and seekers can learn authentic Vedic
              Astrology and apply its wisdom for personal growth and service to society.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER ------------------------------------------------- */}
      <section data-testid="what-we-offer" className="py-24 md:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="eyebrow mb-3">What We Offer</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">A complete <span className="gold-text italic">Vedic ecosystem.</span></h2>
            <p className="text-foreground/70 mt-4">From consultations to courses, remedies to writings — everything a modern seeker needs.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {offerings.map((o, i) => (
              <div key={o.title} data-testid={`offer-${i}`} className="group p-6 rounded-3xl bg-background border border-foreground/10 hover:border-[#D4AF37]/60 transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className="w-11 h-11 rounded-2xl grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-5 group-hover:bg-[#10B981]/15 group-hover:border-[#10B981]/40 transition-colors">
                  <o.icon className="w-5 h-5 text-[#D4AF37] group-hover:text-[#0E9F6E] transition-colors" />
                </div>
                <h3 className="font-serif text-xl leading-tight mb-2">{o.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{o.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE ---------------------------------------------------- */}
      <section data-testid="why-choose" className="py-24 bg-[#0B1F3A] text-[#F5E8D8] relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#10B981]/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37]/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="eyebrow mb-3">Why Choose YourAstroHub?</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">
              Nine reasons seekers <span className="gold-text italic">stay with us.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reasons.map((r, i) => (
              <div key={r.title} data-testid={`reason-${i}`} className="group flex items-center gap-4 p-5 rounded-2xl bg-[#0F284B]/70 backdrop-blur border border-[#D4AF37]/20 hover:border-[#D4AF37]/60 transition-all hover:-translate-y-1">
                <div className="shrink-0 w-11 h-11 rounded-full grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40">
                  <r.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="font-serif text-lg md:text-xl leading-tight">{r.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE VALUES --------------------------------------------------- */}
      <section data-testid="core-values" className="py-24 md:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="eyebrow mb-3">Our Core Values</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">The compass guiding <span className="emerald-text italic">every session.</span></h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, i) => (
              <div key={v.title} data-testid={`value-${i}`} className="p-8 rounded-3xl bg-background border border-foreground/10 hover:border-[#10B981]/60 transition-colors">
                <div className="w-11 h-11 rounded-2xl grid place-items-center bg-[#10B981]/10 border border-[#10B981]/30 mb-5">
                  <v.icon className="w-5 h-5 text-[#0E9F6E]" />
                </div>
                <h3 className="font-serif text-2xl mb-2">{v.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA ----------------------------------------------------------- */}
      <section data-testid="about-cta" className="pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 bg-[#0B1F3A] text-[#F5E8D8] grain text-center">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#10B981]/25 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#D4AF37]/25 blur-3xl rounded-full" />
            <div className="relative">
              <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-serif text-4xl md:text-6xl font-light leading-tight max-w-3xl mx-auto">
                Ready to Discover <span className="gold-text italic">Your Life&rsquo;s Potential?</span>
              </h3>
              <p className="mt-5 text-[#F5E8D8]/75 max-w-2xl mx-auto text-lg leading-relaxed">
                Whether you are seeking answers to life&rsquo;s challenges or wish to master Vedic Astrology,
                YourAstroHub is here to guide you every step of the way.
              </p>
              <div className="mt-9 flex flex-wrap gap-3 justify-center">
                <Link to="/consultations" data-testid="about-cta-book">
                  <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                    Book a Consultation <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/courses" data-testid="about-cta-courses">
                  <Button size="lg" variant="outline" className="rounded-full border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 px-8 py-6">
                    Explore Our Courses
                  </Button>
                </Link>
                <a href={waLink("Hello Bhupaal ji, I would like to know more about YourAstroHub.")} target="_blank" rel="noreferrer" data-testid="about-cta-whatsapp">
                  <Button size="lg" className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white px-8 py-6">
                    <MessageCircle className="w-4 h-4 mr-2" />Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
